import { WORKSPACE_ID_HEADER } from '@mindkeep/shared'
import { z, type ZodTypeAny } from 'zod'
import { env } from './env'
import { supabase } from './supabase'

class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

let requestWorkspaceId: string | null = null

export function setRequestWorkspaceId(id: string | null): void {
  requestWorkspaceId = id && id.length > 0 ? id : null
}

export function getRequestWorkspaceId(): string | null {
  return requestWorkspaceId
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** Central `x-workspace-id` header for authenticated API calls (D-S1B2a-11). */
function workspaceIdHeader(): Record<string, string> {
  return requestWorkspaceId ? { [WORKSPACE_ID_HEADER]: requestWorkspaceId } : {}
}

export async function buildAuthedHeaders(
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  return {
    ...extra,
    ...(await getAuthHeader()),
    ...workspaceIdHeader(),
  }
}

export interface ApiRequestOptions<TSchema extends ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  body?: unknown
  schema: TSchema
  signal?: AbortSignal
}

export async function apiRequest<TSchema extends ZodTypeAny>(
  options: ApiRequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  const { method = 'GET', path, body, schema, signal } = options
  const headers = await buildAuthedHeaders({ 'Content-Type': 'application/json' })

  // Always route through the /api prefix; tolerate callers that included it.
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')
  const cleanPath = path.replace(/^\/?(api\/)?/, '/')
  const url = `${baseUrl}/api${cleanPath}`

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  const text = await response.text()
  const json = text ? (JSON.parse(text) as unknown) : null

  if (!response.ok) {
    const errSchema = z.object({
      error: z.object({ code: z.string(), message: z.string() }),
    })
    const parsedError = errSchema.safeParse(json)
    if (parsedError.success) {
      throw new ApiError(
        response.status,
        parsedError.data.error.code,
        parsedError.data.error.message,
      )
    }
    throw new ApiError(response.status, 'unknown_error', response.statusText)
  }

  return schema.parse(json)
}

export interface ApiMultipartOptions<TSchema extends ZodTypeAny> {
  method?: 'POST' | 'PATCH'
  path: string
  formData: FormData
  schema: TSchema
  signal?: AbortSignal
}

/**
 * Multipart-Variante. WICHTIG: Setzt absichtlich KEIN Content-Type — der Browser
 * setzt den Boundary-Header automatisch beim Senden von FormData.
 */
export async function apiRequestMultipart<TSchema extends ZodTypeAny>(
  options: ApiMultipartOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  const { method = 'POST', path, formData, schema, signal } = options
  const headers = await buildAuthedHeaders()

  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')
  const cleanPath = path.replace(/^\/?(api\/)?/, '/')
  const url = `${baseUrl}/api${cleanPath}`

  const response = await fetch(url, {
    method,
    headers,
    body: formData,
    signal,
  })

  const text = await response.text()
  const json = text ? (JSON.parse(text) as unknown) : null

  if (!response.ok) {
    const errSchema = z.object({
      error: z.object({ code: z.string(), message: z.string() }),
    })
    const parsedError = errSchema.safeParse(json)
    if (parsedError.success) {
      throw new ApiError(
        response.status,
        parsedError.data.error.code,
        parsedError.data.error.message,
      )
    }
    throw new ApiError(response.status, 'unknown_error', response.statusText)
  }

  return schema.parse(json)
}

/**
 * Binaerer Download (RNA M6: Nachweis-PDF). Eigene Funktion statt eines Flags
 * an `apiRequest`, weil hier bewusst kein Zod-Schema greift — die Antwort ist
 * kein JSON. Der Fehlerpfad bleibt derselbe: das Backend antwortet auch bei
 * einem Download-Endpunkt mit dem JSON-Fehlerobjekt.
 */
export async function apiRequestBlob(options: {
  path: string
  signal?: AbortSignal
}): Promise<{ blob: Blob; filename: string | null }> {
  const headers = await buildAuthedHeaders()

  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')
  const cleanPath = options.path.replace(/^\/?(api\/)?/, '/')
  const url = `${baseUrl}/api${cleanPath}`

  const response = await fetch(url, { headers, ...(options.signal ? { signal: options.signal } : {}) })

  if (!response.ok) {
    const text = await response.text()
    const errSchema = z.object({ error: z.object({ code: z.string(), message: z.string() }) })
    const parsed = errSchema.safeParse(text ? (JSON.parse(text) as unknown) : null)
    if (parsed.success) {
      throw new ApiError(response.status, parsed.data.error.code, parsed.data.error.message)
    }
    throw new ApiError(response.status, 'unknown_error', response.statusText)
  }

  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = disposition.match(/filename="([^"]+)"/)
  return { blob: await response.blob(), filename: match?.[1] ?? null }
}

export { ApiError }
